import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import { generateCOAPDF } from '../utils/pdfGenerator';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';

const STATUSES = ['draft', 'approved'];
const ATTACHMENT_OPTIONS = ['NMR', 'MASS', 'GC', 'HPLC', 'TGA', 'IR', 'XRPD', 'CHN', 'DSC'];
const CAT_CATEGORIES = ['impurity_standard', 'reference_standard', 'metabolite', 'degradation_product', 'synthesis_intermediate', 'custom'];
const UPLOADS_BASE = '/uploads/coa-images';
const statusColors = { draft: 'warning', approved: 'success' };

const DownloadIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const EyeIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const EditIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
    <path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
  </svg>
);
const PlusIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const emptyForm = () => ({
  catalog_item_id: '', product_name: '', iupac_name: '', cas_number: '', catalog_number: '',
  batch_number: '', molecular_formula: '', molecular_weight: '',
  date_of_analysis: '', retest_date: '',
  storage_conditions: 'Store at refrigerator (2-8°C) for long term storage',
  shipping_conditions: 'Product is stable to be shipped at room temperature',
  attachments: '', status: 'draft', reviewer1_name: '', reviewer2_name: '', notes: '',
  tests: [{ test_name: '', specification: '', result: '' }],
});

const emptyNewProduct = () => ({
  name: '', catalog_number: '', cas_number: '', molecular_formula: '', molecular_weight: '',
  category: 'impurity_standard', purity: '', storage: '', description: '', prasi_lab_catalog: false,
});

// ── Product search combobox ────────────────────────────────────────────────────
function ProductSelector({ catalogItems, value, onSelect, onNewProduct }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = catalogItems.find(c => String(c.id) === String(value));

  useEffect(() => {
    if (selected) setQuery(`${selected.name}${selected.catalog_number ? ` [${selected.catalog_number}]` : ''}`);
  }, [selected]);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? catalogItems.filter(c =>
      c.name.toLowerCase().includes(query.toLowerCase()) ||
      (c.catalog_number || '').toLowerCase().includes(query.toLowerCase()) ||
      (c.cas_number || '').toLowerCase().includes(query.toLowerCase())
    ).slice(0, 20)
    : catalogItems.slice(0, 20);

  const handleSelect = (item) => {
    setQuery(`${item.name}${item.catalog_number ? ` [${item.catalog_number}]` : ''}`);
    setOpen(false);
    onSelect(item);
  };

  const handleClear = () => {
    setQuery('');
    setOpen(false);
    onSelect(null);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative', flex: 1 }}>
        <input
          className="form-control"
          placeholder="Search product name, catalog #, CAS #…"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onSelect(null); }}
          onFocus={() => setOpen(true)}
        />
        {value && (
          <button type="button" onClick={handleClear}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16, lineHeight: 1 }}>
            ×
          </button>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)', maxHeight: 280, overflowY: 'auto',
        }}>
          <div onClick={() => { setOpen(false); onNewProduct(); }}
            style={{ padding: '9px 14px', cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, fontSize: '0.86rem', borderBottom: '1px solid var(--border)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-lighter)'}
            onMouseLeave={e => e.currentTarget.style.background = ''}>
            ＋ Add New Product…
          </div>
          {filtered.map(item => (
            <div key={item.id} onClick={() => handleSelect(item)}
              style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-lighter)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <div style={{ fontWeight: 600, fontSize: '0.86rem' }}>{item.name}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {[item.catalog_number && `Cat: ${item.catalog_number}`, item.cas_number && `CAS: ${item.cas_number}`].filter(Boolean).join('  ·  ')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── New Product inline form ───────────────────────────────────────────────────
function NewProductForm({ onConfirm, onCancel, canCreate }) {
  const [np, setNp] = useState(emptyNewProduct());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setF = (k, v) => setNp(p => ({ ...p, [k]: v }));

  const handleConfirm = async () => {
    if (!np.name.trim()) { setErr('Product name is required.'); return; }
    setSaving(true); setErr('');
    try {
      if (canCreate) {
        const { data: res } = await api.post('/catalog', { ...np, category: np.category || 'impurity_standard' });
        onConfirm(res.data, true);
      } else {
        // User can't create catalog items — just use the data to fill COA fields
        onConfirm({ ...np, id: null }, false);
      }
    } catch (e) {
      setErr(e?.response?.data?.message || 'Failed to create product.');
      setSaving(false);
    }
  };

  return (
    <div style={{ border: '2px solid var(--primary)', borderRadius: 10, padding: 16, marginBottom: 16, background: 'var(--primary-lighter)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary)' }}>
          New Product {canCreate ? '(will be added to Catalog)' : '(fill details — catalog creation requires admin/manager/scientist role)'}
        </span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
      {err && <div style={{ color: 'var(--error)', fontSize: '0.8rem', marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ gridColumn: '1/-1' }}>
          <label className="form-label">Product Name <span style={{ color: 'var(--error)' }}>*</span></label>
          <input className="form-control" value={np.name} onChange={e => setF('name', e.target.value)} placeholder="e.g. 1-Bromo-2-methoxy-4-methyl-5-nitrobenzene" />
        </div>
        <div>
          <label className="form-label">Catalog #</label>
          <input className="form-control" value={np.catalog_number} onChange={e => setF('catalog_number', e.target.value)} placeholder="e.g. PL0719" />
        </div>
        <div>
          <label className="form-label">CAS #</label>
          <input className="form-control" value={np.cas_number} onChange={e => setF('cas_number', e.target.value)} placeholder="e.g. 123-45-6" />
        </div>
        <div>
          <label className="form-label">Molecular Formula</label>
          <input className="form-control" value={np.molecular_formula} onChange={e => setF('molecular_formula', e.target.value)} placeholder="e.g. C8H8BrNO3" />
        </div>
        <div>
          <label className="form-label">Molecular Weight</label>
          <input className="form-control" value={np.molecular_weight} onChange={e => setF('molecular_weight', e.target.value)} placeholder="e.g. 260.06" />
        </div>
        <div>
          <label className="form-label">Category</label>
          <select className="form-control form-select" value={np.category} onChange={e => setF('category', e.target.value)}>
            {CAT_CATEGORIES.map(c => <option key={c} value={c}>{c.split('_').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Purity</label>
          <input className="form-control" value={np.purity} onChange={e => setF('purity', e.target.value)} placeholder="e.g. ≥98%" />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <label className="form-label">Storage Conditions</label>
          <input className="form-control" value={np.storage} onChange={e => setF('storage', e.target.value)} placeholder="e.g. Store at -20°C" />
        </div>
      </div>
      <div style={{ marginTop: 10, marginBottom: 6 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', fontWeight: 500, fontSize: '0.875rem' }}>
          <input type="checkbox" checked={!!np.prasi_lab_catalog} onChange={e => setF('prasi_lab_catalog', e.target.checked)} style={{ width: 16, height: 16 }} />
          PrasiLab Catalog <span style={{ fontWeight: 400, fontSize: '0.8rem', color: 'var(--text-muted)' }}>(made by us; uncheck if vendor product)</span>
        </label>
      </div>
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={saving}>
          {saving ? 'Adding…' : canCreate ? 'Add to Catalog & Use' : 'Use This Product'}
        </button>
      </div>
    </div>
  );
}

// ── Attachment tag picker ─────────────────────────────────────────────────────
function AttachmentPicker({ value, onChange }) {
  const [custom, setCustom] = useState('');
  const selected = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];

  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
    onChange(next.join(', '));
  };

  const addCustom = () => {
    const trimmed = custom.trim();
    if (!trimmed || selected.includes(trimmed)) { setCustom(''); return; }
    onChange([...selected, trimmed].join(', '));
    setCustom('');
  };

  // Custom tags = those not in predefined list
  const customTags = selected.filter(s => !ATTACHMENT_OPTIONS.includes(s));

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {ATTACHMENT_OPTIONS.map(opt => (
          <button key={opt} type="button" onClick={() => toggle(opt)}
            style={{
              padding: '3px 10px', borderRadius: 20, fontSize: '0.78rem', cursor: 'pointer',
              border: `1px solid ${selected.includes(opt) ? 'var(--primary)' : 'var(--border)'}`,
              background: selected.includes(opt) ? 'var(--primary)' : 'var(--bg-secondary)',
              color: selected.includes(opt) ? '#fff' : 'var(--text-primary)',
              fontWeight: selected.includes(opt) ? 600 : 400,
            }}>
            {opt}
          </button>
        ))}
        {customTags.map(tag => (
          <span key={tag} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 10px', borderRadius: 20, fontSize: '0.78rem',
            border: '1px solid var(--primary)', background: 'var(--primary)',
            color: '#fff', fontWeight: 600,
          }}>
            {tag}
            <button type="button" onClick={() => toggle(tag)}
              style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 14 }}>×</button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="form-control"
          style={{ flex: 1, fontSize: '0.82rem' }}
          placeholder="Type custom attachment and press Enter…"
          value={custom}
          onChange={e => setCustom(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
        />
        <button type="button" className="btn btn-secondary btn-sm" onClick={addCustom}
          style={{ whiteSpace: 'nowrap' }}>Add</button>
      </div>
    </div>
  );
}

// ── Test rows ─────────────────────────────────────────────────────────────────
function TestsTable({ tests, onChange }) {
  const update = (idx, field, val) => onChange(tests.map((t, i) => i === idx ? { ...t, [field]: val } : t));
  const addRow = () => onChange([...tests, { test_name: '', specification: '', result: '' }]);
  const removeRow = (idx) => onChange(tests.filter((_, i) => i !== idx));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label className="form-label" style={{ margin: 0 }}>Test Results</label>
        <button type="button" className="btn btn-secondary btn-sm" onClick={addRow}><PlusIcon /> Add Row</button>
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)' }}>
              {['Test', 'Specification', 'Result', ''].map((h, i) => (
                <th key={i} style={{ padding: '6px 8px', fontSize: '0.75rem', fontWeight: 600, textAlign: 'left', borderBottom: '1px solid var(--border)', width: i === 0 ? '35%' : i === 1 ? '38%' : i === 2 ? '22%' : '5%' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tests.map((t, i) => (
              <tr key={i} style={{ borderBottom: i < tests.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <td style={{ padding: '4px 6px' }}>
                  <input className="form-control" style={{ fontSize: '0.82rem' }} value={t.test_name}
                    onChange={e => update(i, 'test_name', e.target.value)} placeholder="e.g. Appearance" />
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <input className="form-control" style={{ fontSize: '0.82rem' }} value={t.specification}
                    onChange={e => update(i, 'specification', e.target.value)} placeholder="e.g. White crystalline solid" />
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <input className="form-control" style={{ fontSize: '0.82rem' }} value={t.result}
                    onChange={e => update(i, 'result', e.target.value)} placeholder="e.g. Complies" />
                </td>
                <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                  <button type="button" className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--error)' }}
                    onClick={() => removeRow(i)}>
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── View Modal ────────────────────────────────────────────────────────────────
function ViewModal({ coa, onClose, onEdit, onDownload }) {
  if (!coa) return null;
  const tests = coa.tests || [];
  return (
    <Modal open onClose={onClose} title={`COA — ${coa.coa_number}`} size="lg"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
        <button className="btn btn-ghost" onClick={onEdit}><EditIcon /> Edit</button>
        <button className="btn btn-primary" onClick={onDownload}><DownloadIcon /> Download PDF</button>
      </>}
    >
      {coa.catalog_item_id && (
        <div style={{ marginBottom: 10, padding: '6px 12px', background: 'var(--primary-lighter)', borderRadius: 6, fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 600 }}>
          Linked to Catalog Item #{coa.catalog_item_id}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', marginBottom: 14 }}>
        {[
          ['COA Number', coa.coa_number],
          ['Status', <span key="s" className={`badge badge-${statusColors[coa.status] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{coa.status}</span>],
          ['Product Name', coa.product_name],
          ['IUPAC Name', coa.iupac_name || '—'],
          ['CAS#', coa.cas_number || '—'],
          ['Catalog#', coa.catalog_number || '—'],
          ['Batch#', coa.batch_number || '—'],
          ['Molecular Formula', coa.molecular_formula || '—'],
          ['Molecular Weight', coa.molecular_weight || '—'],
          ['Date of Analysis', coa.date_of_analysis ? new Date(coa.date_of_analysis).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'],
          ['Retest Date', coa.retest_date ? new Date(coa.retest_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'],
          ['Reviewer 1', coa.reviewer1_name || '—'],
          ['Reviewer 2', coa.reviewer2_name || '—'],
        ].map(([label, value]) => (
          <div key={label}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: '0.87rem', color: 'var(--text-primary)' }}>{value}</div>
          </div>
        ))}
      </div>
      {coa.storage_conditions && <div style={{ marginBottom: 8 }}><div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>Storage Conditions</div><div style={{ fontSize: '0.85rem' }}>{coa.storage_conditions}</div></div>}
      {coa.shipping_conditions && <div style={{ marginBottom: 12 }}><div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>Shipping Conditions</div><div style={{ fontSize: '0.85rem' }}>{coa.shipping_conditions}</div></div>}
      {tests.length > 0 && (
        <table className="table" style={{ marginBottom: 10 }}>
          <thead><tr><th>Test</th><th>Specification</th><th>Result</th></tr></thead>
          <tbody>{tests.map((t, i) => <tr key={i}><td style={{ fontWeight: 500 }}>{t.test_name || '—'}</td><td style={{ color: 'var(--text-secondary)' }}>{t.specification || '—'}</td><td><span style={{ fontWeight: 600, color: 'var(--success)' }}>{t.result || '—'}</span></td></tr>)}</tbody>
        </table>
      )}
      {coa.attachments && <div style={{ fontSize: '0.84rem', color: 'var(--text-secondary)' }}><strong>Attachments:</strong> {coa.attachments}</div>}
      {coa.structure_image && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Structure / Product Image</div>
          <img
            src={`${UPLOADS_BASE}/${coa.structure_image}`}
            alt="structure"
            style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 8, padding: 8, background: 'var(--bg-secondary)' }}
          />
        </div>
      )}
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function COA() {
  const { user, isPrivileged: ctxPrivileged, userRoles } = useAuth();
  const isPrivileged = ctxPrivileged;
  const canCreateCat = ['admin', 'manager', 'scientist'].some(r => (userRoles || []).includes(r));

  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 15 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [catalogItems, setCatalogItems] = useState([]);
  const [viewCoa, setViewCoa] = useState(null);
  const [modal, setModal] = useState({ open: false, mode: 'add', editId: null });
  const [form, setForm] = useState(emptyForm());
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copyingTests, setCopyingTests] = useState(false);
  const [imageFile, setImageFile] = useState(null);      // File object to upload
  const [imagePreview, setImagePreview] = useState(null);   // data URL for preview
  const [existingImage, setExistingImage] = useState(null); // filename from server
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/coa', {
        params: { page, limit: 15, search: debouncedSearch || undefined, status: statusFilter || undefined },
      });
      setData(res);
    } finally { setLoading(false); }
  }, [page, debouncedSearch, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/catalog/active').then(r => setCatalogItems(r.data.data || [])).catch(() => { });
  }, []);

  const setField = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const loadCatalogImage = async (imageFilename) => {
    try {
      const res = await fetch(`/products/${imageFilename}`);
      if (!res.ok) return;
      const blob = await res.blob();
      const ext = imageFilename.split('.').pop().toLowerCase();
      const mimeType = ext === 'png' ? 'image/png' : ext === 'gif' ? 'image/gif' : 'image/jpeg';
      const file = new File([blob], imageFilename, { type: mimeType });
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = ev => setImagePreview(ev.target.result);
      reader.readAsDataURL(blob);
    } catch { /* skip if image unavailable */ }
  };

  const fillFromCatalog = (item) => {
    if (!item) { setField('catalog_item_id', ''); return; }
    setForm(p => ({
      ...p,
      catalog_item_id: item.id || '',
      product_name: item.name || p.product_name,
      cas_number: item.cas_number || p.cas_number,
      catalog_number: item.catalog_number || p.catalog_number,
      molecular_formula: item.molecular_formula || p.molecular_formula,
      molecular_weight: item.molecular_weight || p.molecular_weight,
      storage_conditions: item.storage || p.storage_conditions,
    }));
    // Auto-populate image from catalog product if no image is already set
    if (item.image && !imageFile && !existingImage) {
      loadCatalogImage(item.image);
    }
  };

  const handleNewProduct = (productData, addedToCatalog) => {
    setShowNewProduct(false);
    fillFromCatalog(productData);
    if (addedToCatalog && productData.id) {
      setCatalogItems(prev => [...prev, productData]);
    }
  };

  const openAdd = () => {
    setForm(emptyForm());
    setShowNewProduct(false);
    setImageFile(null); setImagePreview(null); setExistingImage(null);
    setError('');
    setModal({ open: true, mode: 'add', editId: null });
  };

  const openEdit = async (c) => {
    const { data: res } = await api.get(`/coa/${c.id}`);
    const d = res.data;
    setForm({
      catalog_item_id: d.catalog_item_id || '',
      product_name: d.product_name || '',
      iupac_name: d.iupac_name || '',
      cas_number: d.cas_number || '',
      catalog_number: d.catalog_number || '',
      batch_number: d.batch_number || '',
      molecular_formula: d.molecular_formula || '',
      molecular_weight: d.molecular_weight || '',
      date_of_analysis: d.date_of_analysis ? d.date_of_analysis.split('T')[0] : '',
      retest_date: d.retest_date ? d.retest_date.split('T')[0] : '',
      storage_conditions: d.storage_conditions || '',
      shipping_conditions: d.shipping_conditions || '',
      attachments: d.attachments || '',
      status: d.status || 'draft',
      reviewer1_name: d.reviewer1_name || '',
      reviewer2_name: d.reviewer2_name || '',
      notes: d.notes || '',
      tests: (d.tests && d.tests.length > 0)
        ? d.tests.map(t => ({ test_name: t.test_name, specification: t.specification, result: t.result }))
        : [{ test_name: '', specification: '', result: '' }],
    });
    setShowNewProduct(false);
    setImageFile(null); setImagePreview(null);
    setExistingImage(d.structure_image || null);
    setError('');
    setModal({ open: true, mode: 'edit', editId: d.id });
  };

  const openView = async (c) => {
    const { data: res } = await api.get(`/coa/${c.id}`);
    setViewCoa(res.data);
  };

  const handleSave = async () => {
    if (!form.product_name.trim()) { setError('Product name is required.'); return; }
    setSaving(true); setError('');
    try {
      // Prepare payload with proper date handling
      const payload = { 
        ...form, 
        tests: form.tests.filter(t => t.test_name.trim()),
        // Convert empty date strings to null for backend
        date_of_analysis: form.date_of_analysis || null,
        retest_date: form.retest_date || null,
        // Convert empty string IDs to null
        catalog_item_id: form.catalog_item_id || null
      };
      let savedId = modal.editId;
      if (modal.mode === 'add') {
        const { data: res } = await api.post('/coa', payload);
        savedId = res.data?.id;
      } else {
        await api.put(`/coa/${modal.editId}`, payload);
      }
      // Upload image if a new one was selected
      if (imageFile && savedId) {
        const fd = new FormData();
        fd.append('image', imageFile);
        await api.post(`/coa/${savedId}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setModal({ open: false, mode: 'add', editId: null });
      load();
    } catch (err) {
      console.error('COA Save Error:', err);
      setError(err?.response?.data?.message || 'Failed to save COA.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/coa/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      load();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete.');
    }
  };

  const handleDownload = async (c) => {
    const { data: res } = await api.get(`/coa/${c.id}`);
    generateCOAPDF(res.data);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Certificate of Analysis</h1>
          <p className="page-subtitle">Manage COA records and generate PDF certificates</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><PlusIcon /> New COA</button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="search-input-wrap" style={{ flex: '1 1 220px' }}>
              <span className="search-icon">
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              </span>
              <input className="form-control" placeholder="Search COA #, product, catalog #, batch…" value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-control form-select" style={{ width: 140 }} value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>COA #</th><th>Product Name</th><th>Catalog #</th><th>Batch #</th>
                <th>Date of Analysis</th><th>Retest Date</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}
              {!loading && data.data.length === 0 && <tr><td colSpan={8}><div className="empty-state"><p>No COA records found.</p></div></td></tr>}
              {!loading && data.data.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.82rem' }}>{c.coa_number}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{c.product_name}</div>
                    {c.iupac_name && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{c.iupac_name}</div>}
                  </td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{c.catalog_number || '—'}</td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{c.batch_number || '—'}</td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    {c.date_of_analysis ? new Date(c.date_of_analysis).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    {c.retest_date ? new Date(c.retest_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td>
                    <span className={`badge badge-${statusColors[c.status] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{c.status}</span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-icon" data-tooltip="View" onClick={() => openView(c)}><EyeIcon /></button>
                      <button className="btn btn-ghost btn-icon" data-tooltip="Edit" onClick={() => openEdit(c)}><EditIcon /></button>
                      <button className="btn btn-ghost btn-icon" data-tooltip="Download PDF" onClick={() => handleDownload(c)}><DownloadIcon /></button>
                      {isPrivileged && (
                        <button className="btn btn-ghost btn-icon" data-tooltip="Freeze" style={{ color: 'var(--error)' }} onClick={() => setDeleteConfirm(c)}><TrashIcon /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />
      </div>

      {/* View Modal */}
      {viewCoa && (
        <ViewModal coa={viewCoa} onClose={() => setViewCoa(null)}
          onEdit={() => { setViewCoa(null); openEdit(viewCoa); }}
          onDownload={() => handleDownload(viewCoa)} />
      )}

      {/* Create / Edit Modal */}
      <Modal
        open={modal.open}
        onClose={() => !saving && setModal({ open: false, mode: 'add', editId: null })}
        title={modal.mode === 'add' ? 'New Certificate of Analysis' : `Edit COA`}
        size="lg"
        footer={<>
          <button className="btn btn-secondary" disabled={saving}
            onClick={() => setModal({ open: false, mode: 'add', editId: null })}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving…' : modal.mode === 'add' ? 'Create COA' : 'Save Changes'}
          </button>
        </>}
      >
        {error && (
          <div style={{ background: 'var(--error-bg)', color: 'var(--error)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.875rem' }}>{error}</div>
        )}

        {/* Product Selector */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
            Select Product
          </div>
          {showNewProduct ? (
            <NewProductForm
              canCreate={canCreateCat}
              onConfirm={handleNewProduct}
              onCancel={() => setShowNewProduct(false)}
            />
          ) : (
            <div style={{ marginBottom: 8 }}>
              <ProductSelector
                catalogItems={catalogItems}
                value={form.catalog_item_id}
                onSelect={fillFromCatalog}
                onNewProduct={() => setShowNewProduct(true)}
              />
              {form.catalog_item_id && (
                <div style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--success)', fontWeight: 600 }}>
                  ✓ Product selected — fields below are pre-filled but editable
                </div>
              )}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
            Product Information
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Product Name <span style={{ color: 'var(--error)' }}>*</span></label>
              <input className="form-control" value={form.product_name} onChange={e => setField('product_name', e.target.value)} placeholder="e.g. 1-Bromo-2-methoxy-4-methyl-5-nitrobenzene" />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">IUPAC Name</label>
              <input className="form-control" value={form.iupac_name} onChange={e => setField('iupac_name', e.target.value)} placeholder="IUPAC systematic name" />
            </div>
            <div>
              <label className="form-label">CAS#</label>
              <input className="form-control" value={form.cas_number} onChange={e => setField('cas_number', e.target.value)} placeholder="e.g. 123-45-6" />
            </div>
            <div>
              <label className="form-label">Catalog#</label>
              <input className="form-control" value={form.catalog_number} onChange={e => setField('catalog_number', e.target.value)} placeholder="e.g. PL0719" />
            </div>
            <div>
              <label className="form-label">Batch#</label>
              <input className="form-control" value={form.batch_number} onChange={e => setField('batch_number', e.target.value)} placeholder="e.g. Nintedanib01" />
            </div>
            <div>
              <label className="form-label">Molecular Formula</label>
              <input className="form-control" value={form.molecular_formula} onChange={e => setField('molecular_formula', e.target.value)} placeholder="e.g. C8H8BrNO3" />
            </div>
            <div>
              <label className="form-label">Molecular Weight</label>
              <input className="form-control" value={form.molecular_weight} onChange={e => setField('molecular_weight', e.target.value)} placeholder="e.g. 260.06" />
            </div>
          </div>
        </div>

        {/* Dates, Conditions & Structure Image */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
            Dates, Conditions & Structure Image
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Date of Analysis</label>
              <input className="form-control" type="date" value={form.date_of_analysis} onChange={e => setField('date_of_analysis', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Retest Date</label>
              <input className="form-control" type="date" value={form.retest_date} onChange={e => setField('retest_date', e.target.value)} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Long-term Storage Conditions</label>
              <input className="form-control" value={form.storage_conditions} onChange={e => setField('storage_conditions', e.target.value)} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Shipping Conditions</label>
              <input className="form-control" value={form.shipping_conditions} onChange={e => setField('shipping_conditions', e.target.value)} />
            </div>
            {/* Structure / Product Image */}
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Structure / Product Image <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>(shown in PDF — JPG, PNG, max 5 MB)</span></label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Preview box */}
                <div style={{
                  width: 110, height: 90, border: '2px dashed var(--border)', borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--bg-secondary)', overflow: 'hidden', flexShrink: 0,
                }}>
                  {imagePreview ? (
                    <img src={imagePreview} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : existingImage ? (
                    <img src={`${UPLOADS_BASE}/${existingImage}`}
                      alt="structure" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.72rem', padding: 8 }}>
                      <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ display: 'block', margin: '0 auto 4px' }}>
                        <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      No image
                    </div>
                  )}
                </div>
                {/* Upload controls */}
                <div style={{ flex: 1 }}>
                  <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', display: 'inline-block' }}>
                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginRight: 4 }}>
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {imagePreview || existingImage ? 'Change Image' : 'Upload Image'}
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={e => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setImageFile(file);
                        const reader = new FileReader();
                        reader.onload = ev => setImagePreview(ev.target.result);
                        reader.readAsDataURL(file);
                      }} />
                  </label>
                  {(imagePreview || existingImage) && (
                    <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 8, color: 'var(--error)' }}
                      onClick={() => { setImageFile(null); setImagePreview(null); setExistingImage(null); }}>
                      Remove
                    </button>
                  )}
                  <div style={{ marginTop: 6, fontSize: '0.73rem', color: 'var(--text-muted)' }}>
                    Molecular structure, product photo, or any relevant image. Displayed in PDF after product info.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Test Results */}
        <div style={{ marginBottom: 16 }}>
          {modal.mode === 'add' && form.catalog_item_id && (
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary btn-sm"
                disabled={copyingTests}
                onClick={async () => {
                  setCopyingTests(true);
                  try {
                    const { data: res } = await api.get(`/coa/product/${form.catalog_item_id}/latest`);
                    if (res.data && res.data.tests && res.data.tests.length > 0) {
                      setField('tests', res.data.tests.map(t => ({ test_name: t.test_name, specification: t.specification, result: '' })));
                    } else {
                      alert('No approved COA found for this product yet.');
                    }
                  } catch { alert('Failed to fetch previous COA.'); }
                  finally { setCopyingTests(false); }
                }}>
                {copyingTests ? 'Loading…' : '⟳ Copy tests from previous batch'}
              </button>
            </div>
          )}
          <TestsTable tests={form.tests} onChange={tests => setField('tests', tests)} />
        </div>

        {/* Attachments & Approval */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
            Attachments & Approval
          </div>
          <div style={{ marginBottom: 12 }}>
            <label className="form-label">Attachments</label>
            <AttachmentPicker value={form.attachments} onChange={v => setField('attachments', v)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Reviewer 1 Name</label>
              <input className="form-control" value={form.reviewer1_name} onChange={e => setField('reviewer1_name', e.target.value)} placeholder="Name / Designation" />
            </div>
            <div>
              <label className="form-label">Reviewer 2 Name</label>
              <input className="form-control" value={form.reviewer2_name} onChange={e => setField('reviewer2_name', e.target.value)} placeholder="Name / Designation" />
            </div>
            <div>
              <label className="form-label">Status</label>
              <select className="form-control form-select" value={form.status} onChange={e => setField('status', e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Internal notes…" />
          </div>
        </div>
      </Modal>

      {/* Freeze Confirm */}
      {deleteConfirm && (
        <Modal open onClose={() => setDeleteConfirm(null)} title="Freeze COA" size="sm"
          footer={<>
            <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={handleDelete}>Freeze</button>
          </>}
        >
          <p>Are you sure you want to freeze <strong>{deleteConfirm.coa_number}</strong>? This cannot be undone.</p>
        </Modal>
      )}
    </div>
  );
}
